/**
 * Captures `?ref=...` (or `?source=...`) on page load and stores it in
 * localStorage for 30 days. Auth & booking flows can then attach this value
 * so we know where each user came from (flyer, Instagram, referral code, etc.).
 *
 * Usage:
 *   import { useReferralTracker, getReferralSource } from './hooks/useReferralTracker';
 *   useReferralTracker();                    // call once at top of App
 *   const source = getReferralSource();      // read anywhere before signup/booking
 */
import { useEffect } from 'react';

const KEY = 'mmc_signup_source';
const TTL_DAYS = 30;

export function useReferralTracker() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      // Accept several common UTM/ref aliases; first hit wins
      const incoming =
        params.get('ref') ||
        params.get('source') ||
        params.get('utm_source') ||
        params.get('utm_campaign');
      if (!incoming) return;
      const clean = String(incoming).slice(0, 60).replace(/[^A-Za-z0-9_\-.]/g, '');
      if (!clean) return;
      const payload = {
        source: clean,
        first_landing_at: new Date().toISOString(),
        first_landing_page: window.location.pathname,
      };
      // Only overwrite if no value or value expired
      const existing = localStorage.getItem(KEY);
      if (existing) {
        try {
          const prev = JSON.parse(existing);
          const ageDays = (Date.now() - new Date(prev.first_landing_at).getTime()) / 86400000;
          if (ageDays < TTL_DAYS && prev.source) return; // keep first-touch attribution
        } catch (_) {
          // fall through and overwrite
        }
      }
      localStorage.setItem(KEY, JSON.stringify(payload));
    } catch (_) {
      // localStorage might be disabled — fail silent
    }
  }, []);
}

export function getReferralSource() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ageDays = (Date.now() - new Date(parsed.first_landing_at).getTime()) / 86400000;
    if (ageDays > TTL_DAYS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed.source || null;
  } catch (_) {
    return null;
  }
}

export function clearReferralSource() {
  try { localStorage.removeItem(KEY); } catch (_) {
    // localStorage might be disabled — fail silent
  }
}

// =====================================================================
// Promo code tracker (?promo=EARLY20)
// Captures launch-blast / marketing promo codes on first landing,
// stores them for 30 days, and lets the booking flow auto-apply them.
// =====================================================================
const PROMO_KEY = 'mmc_promo_code';

export function usePromoTracker() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const incoming = params.get('promo') || params.get('promo_code');
      if (!incoming) return;
      const clean = String(incoming).toUpperCase().slice(0, 30).replace(/[^A-Z0-9_-]/g, '');
      if (!clean) return;
      localStorage.setItem(PROMO_KEY, JSON.stringify({
        code: clean,
        captured_at: new Date().toISOString(),
      }));
    } catch (_) { /* fail silent */ }
  }, []);
}

export function getPromoCode() {
  try {
    const raw = localStorage.getItem(PROMO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ageDays = (Date.now() - new Date(parsed.captured_at).getTime()) / 86400000;
    if (ageDays > TTL_DAYS) {
      localStorage.removeItem(PROMO_KEY);
      return null;
    }
    return parsed.code || null;
  } catch (_) { return null; }
}

export function clearPromoCode() {
  try { localStorage.removeItem(PROMO_KEY); } catch (_) { /* fail silent */ }
}
